import { memo, useMemo } from 'react';

import Divider from '@zenbusiness/zui/Divider';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import CheckDepositRow from '#client/components/CheckDepositHistory/components/CheckDepositRow';

import TransactionRow from './TransactionRow';

function MonthTransaction({ month, year, transactions }) {
  const transactionRows = useMemo(
    () =>
      transactions.map(
        ({
          // Transaction Details
          id,
          createdAt,
          summary,
          direction,
          amount,
          type,
          reason,
          balance,
          checkDepositId,
          description,
          cardId,
          counterpartyId,
          from,
          // To
          to,
          // Merchant details
          paymentMethod,
          digitalWallet,
          cardPresent,
          recurring,
          verificationMethod,
          merchantName,
          merchantLocation,
          merchantCategory,
          // ATM Details
          atmName,
          atmLocation,
          surcharge,
          // FX Details
          currencyConversion
        }) =>
          checkDepositId ? (
            <CheckDepositRow
              amount={amount.formattedAmount}
              balance={balance.formattedAmount}
              completedDate={createdAt}
              direction={direction}
              id={checkDepositId}
              isCheckDeposit={false}
              key={id}
              postedDate={createdAt}
              reason={reason}
              summary={summary}
              transactionId={id}
              type={type}
            />
          ) : (
            <TransactionRow
              amount={amount.formattedAmount}
              atmLocation={atmLocation}
              // ATM Details
              atmName={atmName}
              balanceAmount={balance.formattedAmount}
              cardId={cardId}
              cardPresent={cardPresent}
              counterpartyIdFromTransaction={counterpartyId}
              createdAt={createdAt}
              // FX Details
              currencyConversion={currencyConversion}
              description={description}
              digitalWallet={digitalWallet}
              direction={direction}
              // From
              from={from}
              id={id}
              key={id}
              merchantCategory={merchantCategory}
              merchantLocation={merchantLocation}
              merchantName={merchantName}
              // Merchant details
              paymentMethod={paymentMethod}
              reason={reason}
              recurring={recurring}
              summary={summary}
              surcharge={surcharge?.formattedAmount}
              // To
              to={to}
              type={type}
              verificationMethod={verificationMethod}
            />
          )
      ),
    [transactions]
  );

  return (
    <Stack
      aria-label="Month transactions"
      direction="column"
      spacing="8px"
    >
      <Text
        color="secondary"
        size="xs"
        uppercase
        weight="medium"
      >
        {`${month} ${year}`}
      </Text>
      <Divider />
      <Stack
        direction="column"
        spacing="8px"
      >
        {transactionRows}
      </Stack>
    </Stack>
  );
}

MonthTransaction.propTypes = {
  month: PropTypes.string.isRequired,
  year: PropTypes.string.isRequired,
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      direction: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
      summary: PropTypes.string.isRequired,
      amount: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        currency: PropTypes.string.isRequired,
        formattedAmount: PropTypes.string.isRequired
      }),
      balance: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        currency: PropTypes.string.isRequired,
        formattedAmount: PropTypes.string.isRequired
      }),
      fromAccount: PropTypes.string,
      description: PropTypes.string,
      toAccount: PropTypes.string,
      traceNumber: PropTypes.string,
      reason: PropTypes.string,
      merchant: PropTypes.string,
      atm: PropTypes.string,
      cardNetwork: PropTypes.string,
      relatedParty: PropTypes.string,
      recipientAccountNumber: PropTypes.string,
      recipientName: PropTypes.string,
      senderAccountNumber: PropTypes.string,
      senderName: PropTypes.string,
      cardId: PropTypes.string,
      counterpartyId: PropTypes.string,
      from: PropTypes.shape({
        accountHolder: PropTypes.string,
        rootingNumber: PropTypes.string,
        fullAccountNumber: PropTypes.string,
        accountNumber: PropTypes.string
      }),
      to: PropTypes.shape({
        accountHolder: PropTypes.string,
        rootingNumber: PropTypes.string,
        fullAccountNumber: PropTypes.string,
        accountNumber: PropTypes.string
      }),
      // Merchant Detail
      paymentMethod: PropTypes.string,
      digitalWallet: PropTypes.string,
      cardPresent: PropTypes.bool,
      recurring: PropTypes.bool,
      verificationMethod: PropTypes.string,
      merchantName: PropTypes.string,
      merchantLocation: PropTypes.string,
      merchantCategory: PropTypes.string,
      // ATM Details
      atmName: PropTypes.string,
      atmLocation: PropTypes.string,
      surcharge: PropTypes.string,
      // FX Details
      currencyConversion: PropTypes.shape({
        originalCurrency: PropTypes.string,
        amountInOriginalCurrency: PropTypes.number,
        fxRate: PropTypes.string
      })
    })
  ).isRequired
};

function areEqual(preProp, nextProp) {
  return (
    preProp.month === nextProp.month &&
    preProp.year === nextProp.year &&
    preProp.transactions === nextProp.transactions
  );
}

export default memo(MonthTransaction, areEqual);
